import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocale } from '@adsk/alloy-react-helpers';
import FormField from '@adsk/alloy-react-form-field';
import SelectBox from '@adsk/alloy-select-box';
import iso3166 from 'iso-3166-2';
import PhoneNumberInput from '@common/PhoneNumberInput';
import AutocompleteOrManualAddress from '@common/AutocompleteOrManualAddress';
import { isEmptyObject } from '@common/utils';
import {
  StyledCountryOption,
  StyledCountryOptionContainer,
  StyledCountryOptionFlag,
  StyledFormField,
  StyledLinkButton,
} from './styles';
import { getStringAddress } from './utils';

const EditCompanyAddress = ({
  address,
  addAddressDisabled,
  disabled,
  errors,
  isInModal,
  mapBoxAccessToken,
  onAddAddressClick,
  onResetAddress,
  setFieldValue,
  statePrefix,
}) => {
  const { ADDRESS_INPUT_MODES } = AutocompleteOrManualAddress;
  const { country, stateOrProvince, phone } = address;
  const [autocompletedAddress, setAutocompletedAddress] = useState();
  const [isMapboxTokenActive, setMapboxTokenActive] = useState(false);
  const [addressInputMode, setAddressInputMode] = useState(
    ADDRESS_INPUT_MODES.AUTOCOMPLETE
  );
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  const countries = useMemo(() => {
    return Object.entries(iso3166.data)
      .map(([key, value]) => ({
        value: key,
        label: value.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, []);
  const countryValue = useMemo(() => {
    return country !== '' && country != null ? { value: country } : null;
  }, [country]);

  const handleCountryChange = (value) => {
    if (addressInputMode === ADDRESS_INPUT_MODES.AUTOCOMPLETE) {
      setAutocompletedAddress('');
      onResetAddress();
    }
    setFieldValue(`${statePrefix}country`, value ? value.value : '');
    if (stateOrProvince != null) {
      setFieldValue(`${statePrefix}stateOrProvince`, null);
    }
    setFieldValue(`${statePrefix}addressLine1`, '');
    setFieldValue(`${statePrefix}addressLine2`, '');
    setFieldValue(`${statePrefix}city`, '');
    setFieldValue(`${statePrefix}postalCode`, '');
  };

  const handleAddressChange = (value) => {
    if (value.addressLine1 === '' || value.addressLine1)
      setFieldValue(`${statePrefix}addressLine1`, value.addressLine1);
    if (value.addressLine2 === '' || value.addressLine2)
      setFieldValue(`${statePrefix}addressLine2`, value.addressLine2);
    if (value.city === '' || value.city)
      setFieldValue(`${statePrefix}city`, value.city);
    if (value.stateOrProvince)
      setFieldValue(`${statePrefix}stateOrProvince`, value.stateOrProvince);
    if (value.country) setFieldValue(`${statePrefix}country`, value.country);
    if (value.postalCode === '' || value.postalCode)
      setFieldValue(`${statePrefix}postalCode`, value.postalCode);
  };

  const handleAddressInputModeChange = (inputMode) => {
    const {
      type: addressType,
      phone: addressPhone,
      country: addressCountry,
      ...restAddress
    } = address;

    setAddressInputMode(inputMode);
    if (inputMode === ADDRESS_INPUT_MODES.MANUAL) setAutocompletedAddress('');
    if (
      inputMode === ADDRESS_INPUT_MODES.AUTOCOMPLETE &&
      isEmptyObject(restAddress)
    )
      setAutocompletedAddress('');
    else if (
      inputMode === ADDRESS_INPUT_MODES.AUTOCOMPLETE &&
      !isEmptyObject(restAddress)
    )
      setAutocompletedAddress(getStringAddress(address));
    onResetAddress();
  };

  useEffect(() => {
    const {
      type: addressType,
      phone: addressPhone,
      country: addressCountry,
      ...restAddress
    } = address;

    setAutocompletedAddress(
      !isEmptyObject(restAddress) ? getStringAddress(address) : ''
    );
  }, [address]);

  return (
    <>
      <StyledFormField
        label={T.translate('COUNTRY_LABEL')}
        labelVariant={FormField.LABEL_VARIANTS.TOP}
        data-testid="EditCompanyAddress_country_field"
      >
        <SelectBox
          clearable
          data-testid="EditCompanyAddress_country_input"
          disabled={disabled}
          maxMenuHeight={180}
          onChange={handleCountryChange}
          options={countries}
          valueRenderer={(item) => (
            <StyledCountryOptionContainer>
              <StyledCountryOption className="react-tel-input">
                <StyledCountryOptionFlag
                  className={`flag ${item.value.toLowerCase()}`}
                />
                <div>{item.label}</div>
              </StyledCountryOption>
            </StyledCountryOptionContainer>
          )}
          optionRenderer={(option) => (
            <StyledCountryOptionContainer>
              <StyledCountryOption className="react-tel-input">
                <StyledCountryOptionFlag
                  className={`flag ${option.value.toLowerCase()}`}
                />
                <div>{option.label}</div>
              </StyledCountryOption>
            </StyledCountryOptionContainer>
          )}
          placeholder={T.translate('COUNTRY_PLACEHOLDER')}
          value={countryValue}
          searchable
        />
      </StyledFormField>
      <StyledFormField
        error={errors.addressLine1}
        errorVariant={FormField.ERROR_VARIANTS.TOP}
        label={T.translate('ADDRESS_LABEL')}
        labelVariant={FormField.LABEL_VARIANTS.TOP}
        data-testid="EditCompanyAddress_street_field"
      >
        <AutocompleteOrManualAddress
          address={address}
          autocompletedAddress={autocompletedAddress}
          autocompleteAddressPlaceholder={T.translate(
            'ADDRESS_AUTOCOMPLETE_PLACEHOLDER'
          )}
          defaultCountry={country}
          disabled={disabled}
          inputMode={addressInputMode}
          isInModal={isInModal}
          isMapboxTokenActive={isMapboxTokenActive}
          mapBoxAccessToken={mapBoxAccessToken}
          onAddressChange={handleAddressChange}
          onAutocompletedAddressResult={setAutocompletedAddress}
          onInputModeChange={handleAddressInputModeChange}
          resetAddressOnDelete={false}
          setMapboxTokenActive={setMapboxTokenActive}
          showManualCountry={false}
        />
      </StyledFormField>
      <StyledFormField
        label={T.translate('PHONE_LABEL')}
        labelVariant={FormField.LABEL_VARIANTS.TOP}
        data-testid="EditCompanyAddress_phone_field"
      >
        <PhoneNumberInput
          onChange={(value, phoneCountry, event, formatted) => {
            setFieldValue(`${statePrefix}phone`, formatted);
          }}
          value={phone}
          error={errors.phone}
          disabled={disabled}
          testId="EditCompanyAddress_phone_input"
        />
      </StyledFormField>
      <StyledLinkButton
        data-testid="EditCompanyAddress_addAddress_button"
        disabled={addAddressDisabled || disabled}
        type="button"
        onClick={onAddAddressClick}
      >
        {T.translate('ADD_ANOTHER_ADDRESS')}
      </StyledLinkButton>
    </>
  );
};

EditCompanyAddress.propTypes = {
  address: PropTypes.shape({
    country: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    stateOrProvince: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
    type: PropTypes.string,
  }),
  addAddressDisabled: PropTypes.bool,
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    country: PropTypes.string,
    addressLine1: PropTypes.string,
    addressLine2: PropTypes.string,
    city: PropTypes.string,
    stateOrProvince: PropTypes.string,
    postalCode: PropTypes.string,
    phone: PropTypes.string,
  }),
  isInModal: PropTypes.bool,
  mapBoxAccessToken: PropTypes.string,
  onAddAddressClick: PropTypes.func.isRequired,
  onResetAddress: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  statePrefix: PropTypes.string,
};

EditCompanyAddress.defaultProps = {
  address: {},
  addAddressDisabled: false,
  disabled: false,
  errors: {},
  isInModal: false,
  mapBoxAccessToken: '',
  onResetAddress: () => {},
  statePrefix: '',
};

export default EditCompanyAddress;
